import { PaymentModuleId } from '@foundation/apis/payments';
import { isValidTheme, Theme } from '@pmi.web/react-theme';

import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import { getWindowParam } from '@/utils/url';

import { DebitMandateDetails } from '../../schemas/DebitMandateDetails';
import { PaymentServicesFailedError } from '../base/errors/PaymentServicesFailedError';
import { TBootstrapMode } from '../base/ServiceRegistrar';
import { Startupable, StartupAuthContext } from '../base/Startupable';
import { registerStartupable } from '../base/StartupHelper';
import { type MandateActivation } from './instruments/MandateReference';
import { PaymentInstrumentsManageStore } from './instruments/PaymentInstrumentsManageStore';
import { PaymentInstrumentsStore } from './instruments/PaymentInstrumentsStore';
import { PaymentIntentStore } from './intent/PaymentIntentStore';
import { getBraintreeClientToken } from '../braintree/helpers/BraintreeApi';
import { excludeBraintreeSepaForSafari } from '../braintree/helpers/BraintreeSepaHelper';

class PaymentStore implements Startupable {
  private _paymentInstrumentsManageStore:
    | PaymentInstrumentsManageStore
    | undefined;
  private _paymentInstrumentsStore!: PaymentInstrumentsStore;
  private _paymentIntentStore: PaymentIntentStore | undefined;

  private _isPreselectedInstrumentPaymentAllowed = true; //flag for paying with preselected from outside instrument - this is needed to avoid infinite loop of re-rendering in case of failure
  private _braintreePaypalToken: string | undefined;
  private _braintreeDefaultToken: string | undefined;
  private _directDebitMandateDetails: DebitMandateDetails | undefined;
  private _mandateActivation: MandateActivation | undefined;
  private _clientId: string | undefined;
  private _authContext: StartupAuthContext | undefined;

  public readonly name = 'PaymentStore';

  constructor() {
    registerStartupable(this, []);
  }

  public async startup(
    mode: TBootstrapMode,
    authContext?: StartupAuthContext,
  ): Promise<void> {
    this._authContext = authContext;

    if (authContext === undefined) {
      throw new Error('Auth context is not set');
    }

    if (mode === 'payment-manage') {
      this._paymentInstrumentsManageStore = new PaymentInstrumentsManageStore();
      await this._paymentInstrumentsManageStore.init(authContext);
    } else {
      this._paymentIntentStore = new PaymentIntentStore();
      await this._paymentIntentStore.startup(authContext.accessToken);
      this._paymentInstrumentsStore = new PaymentInstrumentsStore(
        this._paymentIntentStore.rawPreSelectedPaymentMethod,
        authContext,
      );
      await this._paymentInstrumentsStore.init(this._paymentIntentStore.id);
    }
  }

  get clientId() {
    return this._clientId ?? '';
  }

  get paymentIntentId() {
    return this._paymentIntentStore?.id ?? '';
  }

  get currency() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.currency;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.currency;
    }

    return '';
  }

  get orderTotal() {
    return this._paymentIntentStore?.orderTotal ?? 0;
  }

  get orderNumber() {
    return this._paymentIntentStore?.orderNumber;
  }

  get theme() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.theme;
    }

    const themeFromUrl = getWindowParam('theme');
    if (themeFromUrl !== undefined && isValidTheme(themeFromUrl)) {
      return themeFromUrl;
    }

    return Theme.PM;
  }

  get cancelUrl() {
    return this._paymentIntentStore?.cancelUrl;
  }

  get redirectUrl() {
    // This one allows to keep backwards compatibility with the intent based manage flow
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.successUrl;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.redirectUrl;
    }

    return undefined;
  }

  get successUrl() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.successUrl;
    }

    return undefined;
  }

  get customerEmail() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.customerEmail;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.customerEmail;
    }

    return '';
  }

  get customerCountryCode() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.customerCountry;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.customerCountry;
    }

    return '';
  }

  get customerStreet() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.customerShippingAddress.streetAddress;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.customerStreet;
    }

    return '';
  }

  get customerCity() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.customerShippingAddress.locality;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.customerCity;
    }

    return '';
  }

  get customerRegion() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.customerShippingAddress.region;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.customerRegion;
    }

    return '';
  }

  get customerZipCode() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.customerShippingAddress.postalCode;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.customerZipCode;
    }

    return '';
  }

  get customerName() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.customerName;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.customerName;
    }

    return '';
  }

  get legalEntity() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.legalEntity;
    }

    const legalEntityFromUrl = getWindowParam('le');
    if (!isNaN(Number(legalEntityFromUrl))) {
      return Number(legalEntityFromUrl);
    }

    return undefined;
  }

  get allowedPaymentModules() {
    if (this._paymentIntentStore !== undefined) {
      const paymentModules = [
        ...this._paymentIntentStore.allowedPaymentModules,
      ];

      return excludeBraintreeSepaForSafari(paymentModules);
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      const paymentModules = [
        ...this._paymentInstrumentsManageStore.allowedPaymentModules,
      ];
      return excludeBraintreeSepaForSafari(paymentModules);
    }

    return [];
  }

  get rawPreSelectedPaymentMethod() {
    return this._paymentIntentStore?.rawPreSelectedPaymentMethod;
  }

  get flow() {
    return this._paymentIntentStore?.flow ?? PaymentIntentFlow.Manage;
  }

  get instruments() {
    if (this._paymentInstrumentsStore !== undefined) {
      return this._paymentInstrumentsStore.instruments;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.instruments;
    }

    return [];
  }

  get preSelectedInstrument() {
    if (this._paymentInstrumentsStore !== undefined) {
      return this._paymentInstrumentsStore.preSelectedInstrument;
    }

    return undefined;
  }

  get preSelectedModule() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.preSelectedModule;
    }

    return this.allowedPaymentModules[0];
  }

  get externalPreselectedInstrument() {
    return this._paymentInstrumentsStore?.externalPreselectedInstrument;
  }

  get isPreselectedInstrumentPaymentAllowed() {
    return this._isPreselectedInstrumentPaymentAllowed;
  }

  get skip3DS() {
    if (this._paymentIntentStore !== undefined) {
      return this._paymentIntentStore.skip3DS;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      return this._paymentInstrumentsManageStore.skip3DS ?? false;
    }

    //by default we should trigger 3ds. Skipping is exceptional
    return false;
  }

  denyPreselectedInstrumentPayment() {
    //set flag to false
    this._isPreselectedInstrumentPaymentAllowed = false;
  }

  set directDebitMandate(value: DebitMandateDetails | undefined) {
    this._directDebitMandateDetails = value;
  }

  get directDebitMandate() {
    return this._directDebitMandateDetails;
  }

  set mandateActivation(value: MandateActivation | undefined) {
    this._mandateActivation = value;
  }

  get mandateActivation() {
    return this._mandateActivation;
  }

  async getBraintreePaypalToken(): Promise<string | undefined> {
    if (this._authContext === undefined) {
      throw new Error('Auth context is not set');
    }

    if (this._braintreePaypalToken !== undefined) {
      return Promise.resolve(this._braintreePaypalToken);
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      this._braintreePaypalToken =
        this._paymentInstrumentsManageStore.braintreeClientTokens?.paypalToken;
      return Promise.resolve(this._braintreePaypalToken);
    }

    if (this.paymentIntentId) {
      return getBraintreeClientToken(
        this.paymentIntentId,
        PaymentModuleId.BraintreePayPal,
        this._authContext.accessToken,
      ).then(({ token }) => {
        this._braintreePaypalToken = token;
        return this._braintreePaypalToken;
      });
    }

    return Promise.reject();
  }

  async getBraintreePaypalPayLaterToken(): Promise<string | undefined> {
    if (this._authContext === undefined) {
      throw new Error('Auth context is not set');
    }

    if (this.paymentIntentId) {
      return getBraintreeClientToken(
        this.paymentIntentId,
        PaymentModuleId.BraintreePayPalPayLater,
        this._authContext.accessToken,
      ).then(({ token, clientId }) => {
        this._clientId = clientId;
        return token;
      });
    }

    return Promise.reject();
  }

  async getBraintreeDefaultToken(): Promise<string | undefined> {
    if (this._authContext === undefined) {
      throw new Error('Auth context is not set');
    }

    if (this._braintreeDefaultToken !== undefined) {
      return Promise.resolve(this._braintreeDefaultToken);
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      this._braintreeDefaultToken =
        this._paymentInstrumentsManageStore.braintreeClientTokens?.defaultToken;
      return Promise.resolve(this._braintreeDefaultToken);
    }

    if (this.paymentIntentId) {
      return getBraintreeClientToken(
        this.paymentIntentId,
        //this could be any non-Paypal Braintree payment module
        PaymentModuleId.BraintreeCard,
        this._authContext.accessToken,
      ).then(({ token: defaultToken }) => {
        this._braintreeDefaultToken = defaultToken;
        return this._braintreeDefaultToken;
      });
    }

    return Promise.reject();
  }

  forceUpdateInstrumentStatus(
    startedModules: ReadonlyArray<PaymentModuleId>,
  ): void {
    if (startedModules.length === 0) {
      throw new PaymentServicesFailedError();
    }

    if (
      this._paymentIntentStore !== undefined &&
      this._paymentInstrumentsStore !== undefined
    ) {
      this._paymentIntentStore.forceUpdateAllowedPaymentMethods(startedModules);
      this._paymentInstrumentsStore.forceUpdateInstrumentStatus(startedModules);
      return;
    }

    if (this._paymentInstrumentsManageStore !== undefined) {
      this._paymentInstrumentsManageStore.forceUpdateAllowedPaymentMethods(
        startedModules,
      );
      return;
    }
  }
}

export default new PaymentStore();
